import { Link, useRouterState } from '@tanstack/react-router'
import {
  Activity,
  ArrowLeft,
  CreditCard as BillingIcon,
  Bot,
  Calendar,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Code,
  CreditCard,
  FileText,
  Gift,
  Home,
  Package,
  Plus,
  Puzzle,
  Radio,
  // Sparkles, // DEPRECATED: Non-Agent Features
  TrendingUp,
  User,
  Users,
  Webhook,
} from 'lucide-react'
import { forwardRef, useImperativeHandle, useState } from 'react'
import { Button } from '@/shared/components/ui/button'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/shared/components/ui/collapsible'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/ui/popover'
import { cn } from '@/shared/lib/utils'
import { useAppStore } from '@/shared/stores/appStore'

const dashboardItems = [
  { title: 'Dashboard', url: '/dashboard', icon: Home },
  { title: 'Monetization', url: '/monetization-dashboard', icon: TrendingUp },
]

interface SidebarItem {
  title: string
  url?: string
  icon: React.ComponentType<{ className?: string }>
  children?: { title: string; url: string }[]
}

const workspaceItems: SidebarItem[] = [
  { title: 'Agents', url: '/agents', icon: Bot },
  // DEPRECATED: Non-Agent Features hidden from UI (see DEPRECATED.md)
  // { title: 'Non-Agent Features', url: '/features', icon: Sparkles },
  {
    title: 'Catalog',
    icon: Package,
    children: [
      { title: 'Plans', url: '/catalog/plans' },
      { title: 'Add-ons', url: '/catalog/addons' },
      { title: 'Coupons', url: '/catalog/coupons' },
      { title: 'Taxes', url: '/catalog/taxes' },
    ],
  },
  { title: 'Customers', url: '/customers', icon: Users },
  { title: 'Invoices', url: '/invoices', icon: FileText },
  { title: 'Payments', url: '/payments', icon: CreditCard },
]

const developerItems = [
  { title: 'API Keys', url: '/developers/api-keys', icon: Code },
  { title: 'Webhooks', url: '/developers/webhooks', icon: Webhook },
  { title: 'Integrations', url: '/developers/integrations', icon: Puzzle },
  { title: 'Signals', url: '/developers/signals', icon: Radio },
]

const settingsItems = [
  { title: 'Account', url: '/settings/account', icon: User },
  { title: 'Team Members', url: '/settings/team', icon: Users },
  { title: 'Billing', url: '/settings/billing', icon: BillingIcon },
  { title: 'Activity', url: '/settings/activity', icon: Activity },
]

interface SidebarProps {
  className?: string
  onSettingsClick?: () => void
}

export const Sidebar = forwardRef<
  { handleSettingsClick: () => void },
  SidebarProps
>(({ className }, ref) => {
  const { sidebarCollapsed, toggleSidebar } = useAppStore()
  const routerState = useRouterState()
  const currentPath = routerState.location.pathname
  const [expandedItems, setExpandedItems] = useState<string[]>(['Catalog'])
  const [showSettings, setShowSettings] = useState(false)

  const handleSettingsClick = () => {
    setShowSettings(true)
  }

  useImperativeHandle(ref, () => ({
    handleSettingsClick,
  }))

  const toggleExpanded = (title: string) => {
    setExpandedItems(prev =>
      prev.includes(title)
        ? prev.filter(item => item !== title)
        : [...prev, title]
    )
  }

  return (
    <aside
      className={cn(
        'h-full bg-background border-r transition-all duration-200 flex flex-col',
        sidebarCollapsed ? 'w-16' : 'w-64',
        className
      )}
    >
      {/* Navigation */}
      <div className='flex-1 overflow-y-auto'>
        {showSettings ? (
          /* Settings Navigation */
          <nav className='px-4 space-y-2 py-6'>
            {/* Back Button */}
            {!sidebarCollapsed && (
              <div className='pb-4'>
                <Button
                  variant='ghost'
                  onClick={() => setShowSettings(false)}
                  className='w-full gap-3 h-10 justify-start px-4'
                >
                  <ArrowLeft className='h-4 w-4' />
                  Back
                </Button>
              </div>
            )}

            {/* Settings Header */}
            {!sidebarCollapsed && (
              <div className='px-4 pb-4'>
                <h2 className='text-lg font-semibold'>Settings</h2>
              </div>
            )}

            {/* Settings Items */}
            <div className='space-y-1'>
              {settingsItems.map(item => (
                <Link key={item.title} to={item.url}>
                  <Button
                    variant='ghost'
                    className={cn(
                      'w-full gap-3 h-10',
                      sidebarCollapsed
                        ? 'justify-center px-2'
                        : 'justify-start px-4',
                      currentPath === item.url && 'bg-muted'
                    )}
                  >
                    <item.icon className='h-4 w-4' />
                    {!sidebarCollapsed && item.title}
                  </Button>
                </Link>
              ))}
            </div>
          </nav>
        ) : (
          /* Main Navigation */
          <nav className='px-4 space-y-2 py-6'>
            {/* Dashboard Section */}
            {dashboardItems.map(item => (
              <Link key={item.title} to={item.url}>
                <Button
                  variant='ghost'
                  className={cn(
                    'w-full gap-3 h-10',
                    sidebarCollapsed
                      ? 'justify-center px-2'
                      : 'justify-start px-4',
                    currentPath === item.url && 'bg-muted'
                  )}
                >
                  <item.icon className='h-4 w-4' />
                  {!sidebarCollapsed && item.title}
                </Button>
              </Link>
            ))}

            {/* Workspace Section */}
            <div className='pt-4'>
              {!sidebarCollapsed && (
                <div className='px-4 pb-2'>
                  <h3 className='text-xs font-semibold text-muted-foreground/70 uppercase tracking-wider'>
                    Workspace
                  </h3>
                </div>
              )}
              <div className='space-y-1'>
                {workspaceItems.map(item => {
                  if (item.children && !sidebarCollapsed) {
                    return (
                      <div key={item.title}>
                        <Collapsible
                          open={expandedItems.includes(item.title)}
                          onOpenChange={() => toggleExpanded(item.title)}
                        >
                          <CollapsibleTrigger asChild>
                            {item.url ? (
                              <Link to={item.url} className='block w-full'>
                                <Button
                                  variant='ghost'
                                  className={cn(
                                    'w-full gap-3 h-10 justify-between px-4',
                                    currentPath === item.url && 'bg-muted'
                                  )}
                                >
                                  <div className='flex items-center gap-3'>
                                    <item.icon className='h-4 w-4' />
                                    {item.title}
                                  </div>
                                  <ChevronDown
                                    className={cn(
                                      'h-4 w-4 transition-transform',
                                      expandedItems.includes(item.title) &&
                                        'rotate-180'
                                    )}
                                  />
                                </Button>
                              </Link>
                            ) : (
                              <Button
                                variant='ghost'
                                className={cn(
                                  'w-full gap-3 h-10 justify-between px-4'
                                )}
                              >
                                <div className='flex items-center gap-3'>
                                  <item.icon className='h-4 w-4' />
                                  {item.title}
                                </div>
                                <ChevronDown
                                  className={cn(
                                    'h-4 w-4 transition-transform',
                                    expandedItems.includes(item.title) &&
                                      'rotate-180'
                                  )}
                                />
                              </Button>
                            )}
                          </CollapsibleTrigger>
                          <CollapsibleContent className='space-y-1 mt-1'>
                            {item.children.map(child => (
                              <Link key={child.title} to={child.url}>
                                <Button
                                  variant='ghost'
                                  className={cn(
                                    'w-full justify-start px-4 ml-6 text-sm h-9',
                                    currentPath === child.url && 'bg-muted'
                                  )}
                                >
                                  {child.title}
                                </Button>
                              </Link>
                            ))}
                          </CollapsibleContent>
                        </Collapsible>
                      </div>
                    )
                  }

                  if (item.children && sidebarCollapsed) {
                    // Show Catalog with popover when collapsed
                    if (item.title === 'Catalog') {
                      return (
                        <div key={item.title}>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                variant='ghost'
                                className={cn(
                                  'w-full gap-3 h-10 justify-center px-2',
                                  item.children.some(
                                    child => currentPath === child.url
                                  ) && 'bg-muted'
                                )}
                              >
                                <item.icon className='h-4 w-4' />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent side='right' className='w-48 p-2'>
                              <div className='space-y-1'>
                                <div className='px-3 py-2 text-sm font-semibold'>
                                  {item.title}
                                </div>
                                {item.children.map(child => (
                                  <Link key={child.title} to={child.url}>
                                    <Button
                                      variant='ghost'
                                      className={cn(
                                        'w-full justify-start px-3 h-9 text-sm',
                                        currentPath === child.url && 'bg-muted'
                                      )}
                                    >
                                      <div className='flex items-center gap-2'>
                                        {child.title === 'Plans' && (
                                          <Calendar className='h-4 w-4' />
                                        )}
                                        {child.title === 'Add-ons' && (
                                          <Plus className='h-4 w-4' />
                                        )}
                                        {child.title === 'Coupons' && (
                                          <Gift className='h-4 w-4' />
                                        )}
                                        {child.title}
                                      </div>
                                    </Button>
                                  </Link>
                                ))}
                              </div>
                            </PopoverContent>
                          </Popover>
                        </div>
                      )
                    }
                  }

                  return (
                    <Link key={item.title} to={item.url}>
                      <Button
                        variant='ghost'
                        className={cn(
                          'w-full gap-3 h-10',
                          sidebarCollapsed
                            ? 'justify-center px-2'
                            : 'justify-start px-4',
                          currentPath === item.url && 'bg-muted'
                        )}
                      >
                        <item.icon className='h-4 w-4' />
                        {!sidebarCollapsed && item.title}
                      </Button>
                    </Link>
                  )
                })}
              </div>
            </div>

            {/* Developers Section */}
            <div className='pt-4'>
              {!sidebarCollapsed && (
                <div className='px-4 pb-2'>
                  <h3 className='text-xs font-semibold text-muted-foreground/70 uppercase tracking-wider'>
                    Developers
                  </h3>
                </div>
              )}
              <div className='space-y-1'>
                {developerItems.map(item => (
                  <Link key={item.title} to={item.url}>
                    <Button
                      variant='ghost'
                      className={cn(
                        'w-full gap-3 h-10',
                        sidebarCollapsed
                          ? 'justify-center px-2'
                          : 'justify-start px-4',
                        currentPath === item.url && 'bg-muted'
                      )}
                    >
                      <item.icon className='h-4 w-4' />
                      {!sidebarCollapsed && item.title}
                    </Button>
                  </Link>
                ))}
              </div>
            </div>
          </nav>
        )}
      </div>

      {/* Collapse/Expand Toggle */}
      <div className='border-t p-4'>
        <Button
          variant='ghost'
          size='sm'
          onClick={toggleSidebar}
          className='h-8 w-8 p-0 hover:bg-muted rounded-md'
        >
          {sidebarCollapsed ? (
            <ChevronRight className='h-4 w-4' />
          ) : (
            <ChevronLeft className='h-4 w-4' />
          )}
        </Button>
      </div>
    </aside>
  )
})

Sidebar.displayName = 'Sidebar'
